/**
 * QuickLogBar — the floating bar shown while quick-log mode is armed.
 *
 * It is the mode's ONLY feedback surface, on purpose: a toast per tap would
 * mean 25 toasts on a warm-up lap. So the bar carries the running counter, the
 * last action with an undo, and the double-tap warning.
 *
 * It sits where the setter's "+ New route" FAB sits (fixed, bottom centre,
 * z-index 60). That is not a coincidence to work around — the FAB is hidden
 * while this is up, because placing a route and logging a lap are opposite
 * intents and both redefine what tapping the map does.
 */
import React, { useState } from "react";
import { F } from "../constants";
import { useTranslation } from "../i18n";
import { isClimbEntry } from "@sendly/shared";
import { computeClimbTotals } from "../utils/climbLog";
import { SELECTABLE, ColorDot, ViewportLayer, BTN_PAD } from "./ui";
import { useAppTheme } from "../contexts/ThemeContext";

export function QuickLogBar({
  todayClimbLog, lastAction, cooldownHit, onUndoLast, onStop, onRemoveClimb,
  routes = [], difficulty = {},
}) {
  const { accent, th } = useAppTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const climbs = todayClimbLog?.climbs || {};
  const totals = todayClimbLog?.totals || computeClimbTotals(climbs);

  // Today's climbs grouped by route, most recently climbed first. A
  // project-day `attempt` entry is not a climb and gets no row: the quick log
  // records only send + repeat (U8).
  const rows = Object.values(climbs).reduce((acc, c) => {
    if (!c || !c.routeId || !isClimbEntry(c)) return acc;
    const hit = acc.find(r => r.routeId === c.routeId);
    if (hit) hit.count += 1;
    else acc.push({ routeId: c.routeId, count: 1, difficulty: c.difficulty, kind: c.kind });
    return acc;
  }, []);

  const nameOf = (routeId) => routes.find(r => r.id === routeId)?.name || "";

  const warn = !!cooldownHit;
  const barBg = warn ? "#b45309" : accent;

  // Only the cooldown warning gets a second line. The "{route} +1" echo that
  // used to sit here grew the bar to two rows on every single tap — motion
  // right under the thumb, for information the counter above already carries.
  // A swallowed tap is different: without a line saying so it reads as the app
  // not registering the tap at all.
  const sub = warn ? t('quickLog.cooldown', { name: cooldownHit.name }) : null;

  return (
    // Above the CSS fullscreen map layer (50), same band as the FAB it replaces.
    // The layer is pointer-transparent; the bar itself takes the taps back.
    <ViewportLayer anchor="bottom" zIndex={60} padding="0 12px calc(env(safe-area-inset-bottom, 0px) + 20px)">
    <div style={{
      pointerEvents: "auto",
      width: "min(400px, calc(100vw - 32px))",
      fontFamily: F,
    }}>
      {open && rows.length > 0 && (
        <div style={{
          background: th.card, border: "1px solid " + th.border, borderRadius: 14,
          padding: 12, marginBottom: 8, maxHeight: "40vh", overflowY: "auto",
          boxShadow: "0 6px 20px rgba(0,0,0,.18)",
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: th.text, marginBottom: 10 }}>
            {t('quickLog.todayTitle', { count: totals.climbs || 0 })}
          </div>
          {rows.map(row => (
            <div key={row.routeId} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 0",
              borderBottom: "1px solid " + th.borderLight,
            }}>
              {/* Was the one dot in the app with a hand-rolled neutral ring —
                  someone hit this exact problem here and solved it locally.
                  `ColorDot` is that fix, derived per colour instead of a fixed
                  grey, so it also works when the fill is mid-grey itself. */}
              <ColorDot color={difficulty[row.difficulty]?.color || "#ccc"} surface={th.card} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: th.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...SELECTABLE }}>
                {nameOf(row.routeId) || t('route.ungraded')}
              </span>
              <span style={{ fontSize: 12, fontWeight: 800, color: accent, width: 30, textAlign: "right" }}>×{row.count}</span>
              <button
                onClick={() => onRemoveClimb?.(row.routeId)}
                aria-label={t('quickLog.removeOne')}
                title={t('quickLog.removeOne')}
                style={{
                  border: "none", background: th.inputBg, color: th.textMuted, fontFamily: F,
                  fontSize: 14, width: 28, height: 28, borderRadius: 8, cursor: "pointer", flexShrink: 0,
                }}>
                −
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
        borderRadius: 999, background: barBg, color: "#fff",
        boxShadow: "0 6px 20px rgba(0,0,0,.18)",
      }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#fff", opacity: 0.9, flexShrink: 0 }} />
        <button
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          style={{ flex: 1, minWidth: 0, background: "none", border: "none", color: "#fff", fontFamily: F, textAlign: "left", cursor: "pointer", padding: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 800, display: "block" }}>
            {t('quickLog.counter', { climbs: totals.climbs || 0, sends: totals.sends || 0 })}
          </span>
          {sub && (
            <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.85, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {sub}
            </span>
          )}
        </button>
        {lastAction && !warn && (
          <button
            onClick={onUndoLast}
            style={{ background: "rgba(255,255,255,.2)", border: "none", color: "#fff", fontFamily: F, fontSize: 12, fontWeight: 700, padding: BTN_PAD.sm, borderRadius: 999, cursor: "pointer", flexShrink: 0 }}>
            {t('quickLog.undo')}
          </button>
        )}
        <button
          onClick={onStop}
          style={{ background: "rgba(255,255,255,.2)", border: "none", color: "#fff", fontFamily: F, fontSize: 12, fontWeight: 700, padding: BTN_PAD.sm, borderRadius: 999, cursor: "pointer", flexShrink: 0 }}>
          {t('quickLog.done')}
        </button>
      </div>
    </div>
    </ViewportLayer>
  );
}
